/**
 * ProgressBar — WAI-ARIA compliant progress indicator.
 *
 * Use for migration rollout %, import/export progress, file upload etc.
 * Color+text ensures colorblind visibility (no color-only state).
 */
import { cn } from '../utils/cn';

export interface ProgressBarProps {
  value: number;
  max?: number;
  label?: string;
  tone?: 'primary' | 'success' | 'warning' | 'danger';
  className?: string;
  /** Show the "x%" label inside/next to the bar. Defaults to true. */
  showValue?: boolean;
}

const TONE_MAP = {
  primary: 'bg-biz-primary',
  success: 'bg-biz-success',
  warning: 'bg-biz-warning',
  danger: 'bg-biz-danger',
};

export function ProgressBar({
  value,
  max = 100,
  label,
  tone = 'primary',
  className,
  showValue = true,
}: ProgressBarProps): JSX.Element {
  const clamped = Math.max(0, Math.min(max, value));
  const pct = max === 0 ? 0 : Math.round((clamped / max) * 100);
  return (
    <div className={cn('w-full', className)}>
      {label || showValue ? (
        <div className="flex items-center justify-between mb-biz-content text-biz-xs text-biz-muted-foreground">
          {label ? <span>{label}</span> : <span />}
          {showValue ? <span className="biz-numeric font-medium">{pct}%</span> : null}
        </div>
      ) : null}
      <div
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label}
        className={cn(
          'relative w-full h-2 rounded-biz-full overflow-hidden',
          'bg-biz-muted',
        )}
      >
        <div
          className={cn('h-full transition-all duration-biz-fast', TONE_MAP[tone])}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
